import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Sparkle, ArrowUpRight, BookOpen, X } from "lucide-react";
import { PRODUCTS, PRODUCT_PRICE_INR } from "@/lib/content";
import { PHONE } from "@/lib/site";
import { Reveal, EASE } from "@/lib/motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const waLink = (product) =>
  `https://wa.me/91${PHONE}?text=${encodeURIComponent(
    `Hello Bloom & Heal — I'd like to order the "${product.name}" ready-mix (${product.tagline}). Please share the next steps.`
  )}`;

export default function Products() {
  const [activeArticle, setActiveArticle] = useState(null);

  const openArticle = (p) => setActiveArticle(p);
  const closeArticle = () => setActiveArticle(null);

  return (
    <section id="shop" className="py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">Ready mixes • For frequent emotional episodes</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight leading-tight">
              Hand-blended remedies, ready when you are.
            </h2>
            <p className="mt-4 text-[#0B302F]/70 leading-relaxed">
              Thoughtfully prepared Bach Flower Remedy blends for the emotional states our
              clients ask us for most often. Each bottle is prepared by Gita and shipped from Mumbai.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 self-start eyebrow text-[#2A5C5A]">
            <Sparkle className="h-4 w-4 text-[#D5B876]" strokeWidth={1.5} />
            <span>Same price · ₹{PRODUCT_PRICE_INR} / bottle</span>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PRODUCTS.map((p, i) => {
            const hasArticle = !!p.article;
            return (
              <motion.div
                key={p.id}
                data-testid={`product-${p.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.08 }}
                className="group relative w-full text-left overflow-hidden rounded-[1.5rem] border border-[#EAE4DA] bg-white hover:border-[#D5B876] transition-colors flex flex-col"
              >
                <div className="relative aspect-square overflow-hidden bg-[#F6F2EB]">
                  <img
                    src={`/products/${p.id}.png`}
                    alt={`${p.name} — amber Bach Flower Remedy dropper bottle`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-white/80 backdrop-blur text-[10px] tracking-[0.28em] uppercase text-[#0B302F]/80">
                    0{i + 1} / {PRODUCTS.length}
                  </div>
                  <div className="absolute top-4 right-4 rounded-full bg-white/90 border border-[#EAE4DA] px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-[#0B302F]">
                    ₹{PRODUCT_PRICE_INR}
                  </div>
                  {hasArticle && (
                    <button
                      type="button"
                      onClick={() => openArticle(p)}
                      data-testid={`product-read-article-${p.id}`}
                      aria-label={`Read the full article on ${p.name}`}
                      className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B302F] text-[#FDFBF7] text-[10px] tracking-[0.24em] uppercase cursor-pointer hover:bg-[#2A5C5A] focus:outline-none focus:ring-2 focus:ring-[#D5B876] focus:ring-offset-2"
                    >
                      <BookOpen className="h-3 w-3" strokeWidth={1.6} /> Read article
                    </button>
                  )}
                </div>

                <div className="p-6 sm:p-7 flex flex-col flex-1">
                  <div className="eyebrow text-[#2A5C5A] mb-2">{p.tagline}</div>
                  <h3 className="font-serif text-2xl sm:text-[1.7rem] text-[#0B302F] leading-tight">
                    {p.name}
                  </h3>
                  <p className="mt-3 text-[#0B302F]/75 leading-relaxed text-sm">{p.body}</p>

                  <div className="mt-6 pt-5 border-t border-[#EAE4DA] flex items-center justify-between">
                    <a
                      href={waLink(p)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`product-order-${p.id}`}
                      className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#0B302F] hover:text-[#2A5C5A] font-medium"
                    >
                      <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Order via WhatsApp
                    </a>
                    <ArrowUpRight
                      className="h-4 w-4 text-[#0B302F]/40 group-hover:text-[#D5B876] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                      strokeWidth={1.5}
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <Reveal className="mt-10 text-center text-sm text-[#0B302F]/60 max-w-2xl mx-auto">
          Not sure which one is for you? Book a{" "}
          <a href="#remedy" className="text-[#2A5C5A] underline decoration-[#D5B876]/60 underline-offset-4 hover:text-[#0B302F]">
            free consultation
          </a>{" "}
          and Gita will help pick, or blend a fully personalised remedy for you.
        </Reveal>
      </div>

      <ArticleDialog product={activeArticle} onClose={closeArticle} />
    </section>
  );
}

function ArticleDialog({ product, onClose }) {
  const open = !!product;
  const article = product?.article;

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        data-testid="product-article-dialog"
        className="max-w-3xl bg-[#FDFBF7] border-[#EAE4DA] max-h-[92vh] overflow-y-auto p-0"
      >
        {product && article ? (
          <div>
            <div className="relative aspect-[16/9] sm:aspect-[16/7] overflow-hidden">
              <img
                src={`/products/${product.id}.png`}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-transparent to-transparent" />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="product-article-close"
                className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/90 border border-[#EAE4DA] flex items-center justify-center hover:bg-white"
              >
                <X className="h-4 w-4 text-[#0B302F]" strokeWidth={1.6} />
              </button>
            </div>
            <div className="px-6 sm:px-10 pb-10 -mt-6 sm:-mt-10 relative">
              <div className="eyebrow text-[#2A5C5A] mb-2">{product.tagline}</div>
              <DialogTitle className="font-serif text-3xl sm:text-4xl text-[#0B302F] leading-tight">
                {product.name}
              </DialogTitle>
              <DialogDescription className="mt-4 text-[#0B302F]/85 leading-relaxed font-sans text-[15px]">
                {article.intro}
              </DialogDescription>

              {article.sections.map((section, si) => (
                <div key={si} className="mt-8">
                  {section.heading && (
                    <h4 className="font-serif text-xl sm:text-2xl text-[#0B302F] mb-1.5">{section.heading}</h4>
                  )}
                  {section.note && (
                    <p className="text-[#0B302F]/65 text-sm italic leading-relaxed mb-4">{section.note}</p>
                  )}
                  {!section.heading && (
                    <div className="eyebrow text-[#D5B876] mb-4">Remedies in this blend</div>
                  )}
                  <ol className="space-y-6">
                    {section.remedies.map((r, i) => (
                      <li key={`${si}-${r.name}-${i}`} className="grid grid-cols-[auto_1fr] gap-4">
                        <span className="font-serif text-2xl text-[#D5B876] tabular-nums leading-none pt-1">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <h4 className="font-serif text-xl text-[#0B302F]">{r.name}</h4>
                          <p className="mt-1.5 text-[#0B302F]/80 leading-relaxed text-[15px]">
                            {r.text}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}

              {article.onion && (
                <div className="mt-10 p-6 rounded-2xl bg-[#F6F2EB]/70 border border-[#EAE4DA]">
                  <div className="eyebrow text-[#2A5C5A] mb-2">The onion-layer method</div>
                  <p className="text-[#0B302F]/85 leading-relaxed">{article.onion}</p>
                </div>
              )}

              {article.conclusion && (
                <div className="mt-8 pt-6 border-t border-[#EAE4DA]">
                  <p className="text-[#0B302F]/85 leading-relaxed font-serif italic text-lg">
                    {article.conclusion}
                  </p>
                </div>
              )}

              {article.note && (
                <p className="mt-6 text-[#0B302F]/50 text-xs leading-relaxed" data-testid="product-article-note">
                  {article.note}
                </p>
              )}

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href={waLink(product)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-article-order"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B302F] text-[#FDFBF7] px-6 h-11 text-xs tracking-[0.22em] uppercase hover:bg-[#2A5C5A]"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Order via WhatsApp
                </a>
                <a
                  href="#remedy"
                  data-testid="product-article-book"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    setTimeout(() => {
                      document.getElementById("remedy")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 150);
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-[#0B302F] text-[#0B302F] px-6 h-11 text-xs tracking-[0.22em] uppercase hover:bg-[#F9EAE8]"
                >
                  Book free consultation
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
